import { createClient } from '@supabase/supabase-js'
import type { Database } from '@horizon/types'

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !anonKey) {
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set — see .env.example')
}

export const ENVIRONMENT = (import.meta.env.VITE_ENVIRONMENT ?? 'development') as
  'development' | 'staging' | 'production'

export const supabaseClient = createClient<Database>(url, anonKey, {
  db: { schema: 'public' },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
})

/**
 * What an edge function accepts as a payload: a JSON object for the ordinary
 * calls, or `FormData` for the ones that carry a file (`media-upload-pdf`).
 * Mirrors the subset of `FunctionInvokeOptions['body']` the admin actually uses.
 */
export type EdgeFunctionBody = Record<string, unknown> | FormData

/**
 * Invoke a Supabase edge function with the caller's JWT attached.
 * Every privileged operation in admin (media URLs, grants, push dispatch) goes
 * through one of these — the browser never holds a service-role key.
 */
export async function invokeEdgeFunction<T>(name: string, body: EdgeFunctionBody): Promise<T> {
  const { data, error } = await supabaseClient.functions.invoke<T>(name, { body })
  if (error) throw error
  if (data === null) throw new Error(`Edge function ${name} returned no payload`)
  return data
}
